document.addEventListener('DOMContentLoaded', () => {
  const DESKTOP_BREAKPOINT = 1024;
  const AUTOPLAY_DELAY = 5000;
  const section = document.querySelector('.pdp-gallery');
  if (!section) return;

  const container = section.querySelector('.product__media.swiper');
  if (!container) return;

  const wrapper = container.querySelector('.swiper-wrapper');
  const paginationEl = container.querySelector('.swiper-pagination');
  const mainImage = container.querySelector('.main-image');

  const mobileVideoTimeByIndex = [];
  const desktopThumbTimeByIndex = [];
  const desktopMainTimeByIndex = [];

  let swiperInstance = null;
  let mobilePrevRealIndex = null;

  let index = 0;
  let timer = null;
  let resizeObserver = null;
  let galleryAutoplayDisabled = false;
  let autoplayChain = false;
  let mainVideoEndedCleanup = null;

  const slides = () =>
    Array.from(wrapper?.querySelectorAll('.swiper-slide') || []);
  const mainItems = () => Array.from(mainImage?.children || []);

  const safePlay = video => {
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play();
  };

  const safePause = video => {
    if (!video) return;
    video.pause();
  };

  const getSlideVideo = slideEl =>
    slideEl ? slideEl.querySelector('video') : null;
  const getMainVideo = i => mainItems()[i]?.querySelector('video') || null;

  function trackGallerySwipe(swiper) {
    dataLayer.push({
      event: 'custom_event',
      event_name: 'gallery_swipe',
      parameter: ''
    });
  }

  function mobileHandleActiveMedia(swiper) {
    if (!swiper || swiper.destroyed) return;

    const realIndex =
      typeof swiper.realIndex === 'number'
        ? swiper.realIndex
        : swiper.activeIndex;

    if (mobilePrevRealIndex !== null && mobilePrevRealIndex !== realIndex) {
      const prevSlide = swiper.slides[swiper.previousIndex];
      const prevVid = getSlideVideo(prevSlide);
      if (prevVid) {
        mobileVideoTimeByIndex[mobilePrevRealIndex] = prevVid.currentTime || 0;
        safePause(prevVid);
      }
    }

    swiper.slides.forEach(slide => {
      const video = getSlideVideo(slide);
      if (video) safePause(video);
    });

    const activeSlide = swiper.slides[swiper.activeIndex];
    const activeVid = getSlideVideo(activeSlide);

    if (activeVid) {
      if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();

      const time = mobileVideoTimeByIndex[realIndex];
      if (
        typeof time === 'number' &&
        time > 0 &&
        Math.abs(activeVid.currentTime - time) > 0.25
      ) {
        activeVid.currentTime = time;
      }

      safePlay(activeVid);
    } else {
      if (swiper.autoplay && !swiper.autoplay.running) swiper.autoplay.start();
    }
    mobilePrevRealIndex = realIndex;
  }

  function preloadSlideImages(root) {
    root.querySelectorAll('img[loading="lazy"]').forEach(img => {
      img.loading = 'eager';
    });
  }

  function initMobile() {
    if (swiperInstance || typeof Swiper === 'undefined') return;

    preloadSlideImages(wrapper);

    mobilePrevRealIndex = null;
    let wasUserInteraction = false;

    swiperInstance = new Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 16,
      loop: true,
      autoplay: { delay: AUTOPLAY_DELAY, disableOnInteraction: false },
      pagination: paginationEl
        ? { el: paginationEl, clickable: true }
        : undefined,
      on: {
        init(swiper) {
          preloadSlideImages(container);
          mobileHandleActiveMedia(swiper);
        },
        sliderMove() {
          wasUserInteraction = true;
        },
        slideChangeTransitionStart(swiper) {
          if (wasUserInteraction) {
            trackGallerySwipe(swiper);
            wasUserInteraction = false;
          }
        },
        slideChangeTransitionEnd(swiper) {
          mobileHandleActiveMedia(swiper);
        }
      }
    });
  }

  function setActiveMain(i) {
    const items = mainItems();
    items[i]?.classList.add('active');
    items.forEach((el, idx) => {
      if (idx !== i) el.classList.remove('active');
    });
  }

  function setActiveThumb(i) {
    slides().forEach(el => el.classList.remove('is-active-thumb'));
    slides()[i]?.classList.add('is-active-thumb');
  }

  function scrollToActiveThumb(i) {
    if (!wrapper) return;
    if (wrapper.scrollHeight <= wrapper.clientHeight) return;

    const el = slides()[i];
    if (!el) return;

    const w = wrapper.getBoundingClientRect();
    const r = el.getBoundingClientRect();

    const topDelta = r.top - w.top;
    const bottomDelta = r.bottom - w.bottom;

    if (topDelta < 0) wrapper.scrollBy({ top: topDelta, behavior: 'smooth' });
    else if (bottomDelta > 0)
      wrapper.scrollBy({ top: bottomDelta, behavior: 'smooth' });
  }

  function trimThumbnailsContainer() {
    if (!mainImage || !wrapper) return;
    const h = Math.round(mainImage.getBoundingClientRect().height);
    if (h) wrapper.style.height = `${h}px`;
  }

  function stopTimer() {
    if (timer) clearInterval(timer);
    timer = null;
    if (mainVideoEndedCleanup) {
      mainVideoEndedCleanup();
      mainVideoEndedCleanup = null;
    }
  }

  function scheduleAutoplay() {
    stopTimer();
    if (galleryAutoplayDisabled) return;

    const s = slides();
    if (!s.length) return;

    const activeMainVid = getMainVideo(index);
    const activeThumbVid = getSlideVideo(s[index]);
    const isVideo = !!(activeMainVid || activeThumbVid);

    if (!isVideo) {
      timer = setTimeout(() => {
        timer = null;
        autoplayChain = true;
        activateIndex(index + 1, false, false);
        autoplayChain = false;
      }, AUTOPLAY_DELAY);
      return;
    }

    const mainVid = getMainVideo(index);
    if (!mainVid) return;

    const prevLoop = mainVid.loop;
    mainVid.loop = false;
    if (autoplayChain) mainVid.currentTime = 0;

    const onEnded = () => {
      mainVid.removeEventListener('ended', onEnded);
      mainVid.loop = prevLoop;
      mainVideoEndedCleanup = null;
      if (galleryAutoplayDisabled) return;
      autoplayChain = true;
      activateIndex(index + 1, false, false);
      autoplayChain = false;
    };

    mainVid.addEventListener('ended', onEnded);
    mainVideoEndedCleanup = () => {
      mainVid.removeEventListener('ended', onEnded);
      mainVid.loop = prevLoop;
    };
  }

  function setupDesktopArrows() {
    if (!paginationEl) return;
    paginationEl.innerHTML = `
      <button type="button" class="thumb-arrow thumb-prev" aria-label="Previous">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10.3337 12.6668L5.66699 8.00016L10.3337 3.3335" stroke="#120C0C" stroke-width="1.06024" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button type="button" class="thumb-arrow thumb-next" aria-label="Next">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M5.66634 12.6668L10.333 8.00016L5.66634 3.3335" stroke="#120C0C" stroke-width="1.06024" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
  }

  function bindArrowEvents() {
    if (!paginationEl) return;

    const prev = paginationEl.querySelector('.thumb-prev');
    const next = paginationEl.querySelector('.thumb-next');

    if (prev) {
      prev.onclick = e => {
        dataLayer.push({
          event: 'custom_event',
          event_name: 'gallery_navigate',
          parameter: ''
        });

        activateIndex(index - 1, true, true);
      };
    }

    if (next) {
      next.onclick = e => {
        dataLayer.push({
          event: 'custom_event',
          event_name: 'gallery_navigate',
          parameter: ''
        });

        activateIndex(index + 1, true, true);
      };
    }
  }

  function desktopSyncVideos(prevIndex, nextIndex) {
    const s = slides();
    const prevThumb = s[prevIndex];
    const nextThumb = s[nextIndex];

    const prevThumbVid = getSlideVideo(prevThumb);
    const nextThumbVid = getSlideVideo(nextThumb);

    const prevMainVid = getMainVideo(prevIndex);
    const nextMainVid = getMainVideo(nextIndex);

    if (prevMainVid) {
      desktopMainTimeByIndex[prevIndex] = prevMainVid.currentTime || 0;
      safePause(prevMainVid);
    }

    if (prevThumbVid) {
      const t = desktopThumbTimeByIndex[prevIndex];
      if (
        typeof t === 'number' &&
        t > 0 &&
        Math.abs(prevThumbVid.currentTime - t) > 0.25
      ) {
        prevThumbVid.currentTime = t;
      }
      safePlay(prevThumbVid);
    }

    if (nextThumbVid) {
      desktopThumbTimeByIndex[nextIndex] = nextThumbVid.currentTime || 0;
      safePause(nextThumbVid);
    }

    if (nextMainVid) {
      const t = desktopMainTimeByIndex[nextIndex];
      if (
        typeof t === 'number' &&
        t > 0 &&
        Math.abs(nextMainVid.currentTime - t) > 0.25
      ) {
        nextMainVid.currentTime = t;
      }
      safePlay(nextMainVid);
    }
  }

  function activateIndex(i, restartTimer = true, userDismissAutoplay = false) {
    const s = slides();
    if (!s.length) return;

    const prev = index;
    index = ((i % s.length) + s.length) % s.length;

    if (userDismissAutoplay) galleryAutoplayDisabled = true;

    setActiveMain(index);
    setActiveThumb(index);
    scrollToActiveThumb(index);

    desktopSyncVideos(prev, index);

    stopTimer();
    if (!galleryAutoplayDisabled) scheduleAutoplay();
  }

  function initDesktop() {
    container.style.overflow = 'hidden';
    if (wrapper) wrapper.style.overflowY = 'auto';

    mainItems().forEach(item => preloadSlideImages(item));

    trimThumbnailsContainer();
    resizeObserver = new ResizeObserver(trimThumbnailsContainer);
    if (mainImage) resizeObserver.observe(mainImage);

    slides().forEach((el, i) => {
      el.addEventListener('click', e => {
        activateIndex(i, true, true);
        if (!e.isTrusted) return;
        const type = el.querySelector('video') ? 'video' : 'image';
        dataLayer.push({
          event: 'custom_event',
          event_name: 'thumbnail_media_click',
          parameter: JSON.stringify({
            content_number: `${Number(i) + 1}`,
            type
          })
        });
      });
    });

    setupDesktopArrows();
    bindArrowEvents();

    setActiveMain(index);
    setActiveThumb(index);

    const activeMainVid = getMainVideo(index);
    if (activeMainVid) safePlay(activeMainVid);

    stopTimer();
    if (!galleryAutoplayDisabled) scheduleAutoplay();
  }

  const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;

  if (isMobile) {
    initMobile();
  } else {
    initDesktop();
  }

  document
    .querySelectorAll('variant-radios .color_swatch_item')
    .forEach(item => {
      item.addEventListener('click', () => {
        const current_color = document.querySelector(
          '.color_swatch_item.color_active'
        );
        if (item == current_color) return;
        const from = document
          .querySelector('.product-color_label .color_value')
          .textContent.replace(/\n/g, '')
          .trim();
        const value = item.dataset.colorName;
        const content_url = section.querySelector('.container').dataset.handle;
        const _pilExtras = readPdpEventExtras();
        dataLayer.push({
          event: 'custom_event',
          event_name: 'product_color_change',
          parameter: JSON.stringify({
            from,
            value,
            content_url,
            type: 'product',
            item_id: _pilExtras.item_id,
            product_collection: _pilExtras.product_collection
          })
        });

        galleryAutoplayDisabled = false;
        if (!isMobile) activateIndex(0, true, false);
        else if (swiperInstance) swiperInstance.slideToLoop(0);
      });
    });

  if (window.initStampedReviews) {
    window.initStampedReviews({ scopeSelector: '.pdp-gallery' });
  }
});
